import {
  useCallback,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useSeqLog } from "@/hooks/telemetry";
import {
  logCreferenceClickBlocked,
  logCreferenceClickEntry,
  runCreferenceExternalClick,
  runCreferenceInternalClick,
} from "./creferenceClickHandlerModel";
import {
  REF_CLICK_DEBUG_FLOW,
  SEQ_LOG_COMPONENT_NAME,
} from "./creferenceInteractionConstants";
import type {
  InternalReferenceActionMap,
  UseCreferenceInteractionInput,
} from "./creferenceInteractionTypes";

interface UseCreferenceClickHandlersInput
  extends Pick<
    UseCreferenceInteractionInput,
    "effectiveEntityType" | "normalizedEntityId" | "onClick"
  > {
  canClick: boolean;
  internalActionByType: InternalReferenceActionMap;
  setIsActionPending: Dispatch<SetStateAction<boolean>>;
  showAlreadyFocusedHint: () => void;
}

interface UseCreferenceClickHandlersResult {
  handleClick: () => void;
}

export function useCreferenceClickHandlers({
  canClick,
  effectiveEntityType,
  internalActionByType,
  normalizedEntityId,
  onClick,
  setIsActionPending,
  showAlreadyFocusedHint,
}: UseCreferenceClickHandlersInput): UseCreferenceClickHandlersResult {
  const seqLog = useSeqLog(SEQ_LOG_COMPONENT_NAME, {
    flow: REF_CLICK_DEBUG_FLOW,
  });
  const handleClick = useCallback(() => {
    logCreferenceClickEntry({
      canClick,
      entityType: effectiveEntityType,
      normalizedEntityId,
      onClick,
      seqLog,
    });

    if (!canClick) {
      logCreferenceClickBlocked({ normalizedEntityId, seqLog });
      return;
    }

    if (runCreferenceExternalClick({ normalizedEntityId, onClick, seqLog })) {
      return;
    }

    setIsActionPending(true);

    try {
      runCreferenceInternalClick({
        action: internalActionByType[effectiveEntityType],
        showAlreadyFocusedHint,
      });
    } finally {
      setIsActionPending(false);
    }
  }, [
    canClick,
    effectiveEntityType,
    internalActionByType,
    normalizedEntityId,
    onClick,
    seqLog,
    setIsActionPending,
    showAlreadyFocusedHint,
  ]);
  return {
    handleClick,
  };
}
